/**
 * MeasureClickTool — one-click voxel capture for the case viewer's 📐 flow.
 *
 * Extends Cornerstone Tools' BaseTool so it lives natively in the tool stack
 * (no overlay hack, no event-race against built-in tools). When active on the
 * primary mouse button, the next mousedown is converted to [z, y, x] voxel
 * coordinates and dispatched to a per-element handler registered by
 * VolumeViewport. The viewport that owns the click element drives the API
 * call (it has the seriesId) — see VolumeViewport.tsx.
 *
 * DRY note: the canvas→world→voxel pattern matches RegionGrowTool's
 * preMouseDownCallback. We don't reuse that tool body because we don't
 * need flood-fill, contour extraction, sessions, or refinement — just the
 * click → voxel handoff.
 */

type Pt3 = [number, number, number];

export interface MeasureClickModifiers {
  /** Shift held → positive refinement of the existing session. */
  shift: boolean;
  /** Ctrl/Meta held → negative refinement (remove region). */
  ctrl: boolean;
}

export interface MeasureClickPosition {
  /** Page-relative pixel coords (clientX/clientY) for floating overlays. */
  pageX: number;
  pageY: number;
}

export type MeasureClickHandler = (
  voxelZYX: Pt3,
  modifiers: MeasureClickModifiers,
  position: MeasureClickPosition,
) => void;

const _handlers = new WeakMap<Element, MeasureClickHandler>();

/** Register (or clear with null) the click handler for a specific viewport element. */
export function setMeasureClickHandler(
  element: Element | null,
  handler: MeasureClickHandler | null,
): void {
  if (!element) return;
  if (handler) _handlers.set(element, handler);
  else _handlers.delete(element);
}

export function createMeasureClickToolClass(cornerstone: any, cornerstoneTools: any): any {
  const { BaseTool } = cornerstoneTools;
  const { getEnabledElement } = cornerstone;
  const csUtils = cornerstone.utilities;

  class MeasureClickTool extends BaseTool {
    static toolName = "MeasureClickTool";

    constructor(toolProps: any = {}, defaultToolProps: any = {}) {
      super(toolProps, {
        name: "MeasureClickTool",
        bindings: [],
        ...defaultToolProps,
      });
    }

    preMouseDownCallback(evt: any): boolean {
      const { element, currentPoints } = evt.detail;
      const handler = _handlers.get(element);
      if (!handler) return false;
      const nativeEvt = evt.detail.event;
      const modifiers: MeasureClickModifiers = {
        shift: !!nativeEvt?.shiftKey,
        ctrl: !!(nativeEvt?.ctrlKey || nativeEvt?.metaKey),
      };

      const enabledElement = getEnabledElement(element);
      if (!enabledElement?.viewport) return false;
      const viewport = enabledElement.viewport as any;
      const imageData = viewport.getImageData?.();
      if (!imageData) return false;

      const canvasPoint = currentPoints?.canvas;
      if (!canvasPoint) return false;
      const worldPoint = viewport.canvasToWorld(canvasPoint);
      const idx = csUtils.transformWorldToIndex(imageData.imageData, worldPoint);
      if (!idx) return false;
      // transformWorldToIndex returns [col, row, slice] in the volume's IJK
      // frame, but every downstream consumer (CaseViewer's measureClickPin,
      // slotState.sliceIndex, backend `bbox.point*_k`) lives in list-index
      // frame — the same frame the SliceNavigator thumb sits in (via
      // VolumeViewport.tsx → kStepToListIndex → setCurrentSlice). On reversed-
      // HTJ2K canonicalized volumes the two frames differ by N-1-K, so feeding
      // idx[2] (K-step) directly hid the pin label on the actual click slice.
      // Resolve the click world-point's slice through getCurrentImageId →
      // imageIds.indexOf, which yields list-index directly. (The click world
      // always lies on the camera's current slice for a camera-aligned
      // canvas click, so getCurrentImageId is the right anchor.)
      const allImageIds: string[] = viewport.getImageIds?.() ?? [];
      const currentImageId: string | undefined = viewport.getCurrentImageId?.();
      const listIdx =
        currentImageId && allImageIds.length > 0 ? allImageIds.indexOf(currentImageId) : -1;
      const sliceIndex = listIdx >= 0 ? listIdx : Math.round(idx[2]);
      const voxelZYX: Pt3 = [sliceIndex, Math.round(idx[1]), Math.round(idx[0])];

      const position: MeasureClickPosition = {
        pageX: nativeEvt?.clientX ?? 0,
        pageY: nativeEvt?.clientY ?? 0,
      };
      handler(voxelZYX, modifiers, position);
      return true; // consume the event
    }
  }

  return MeasureClickTool;
}
